import {
  QUAOM_FORBIDDEN_URL_TOKEN_PARAM_NAMES,
  QUAOM_SECRET_REDACTION_KEYS,
} from '@quaom/contracts/security';

const SENSITIVE_KEY_PATTERN = new RegExp(
  QUAOM_SECRET_REDACTION_KEYS.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
  'i',
);

const TOKEN_VALUE_PATTERNS: RegExp[] = [
  /(#[^\s]*?(?:manage|token|delete|claim|supportProof)=)[^\s&]+/gi,
  /([?&](?:manage|token|manageToken|deleteToken|claimToken|supportProof|authorization|session)=)[^\s&#]+/gi,
  /((?:manage|delete|claim|supportProof|token)[_-]?token["'\s:=]+)[^"'\s&,}]+/gi,
  /(authorization["'\s:=]+bearer\s+)[^"'\s&,}]+/gi,
  /(Bearer\s+)[A-Za-z0-9._~+\/-]+/g,
];

const FORBIDDEN_URL_PARAM_NAMES = new Set<string>(
  QUAOM_FORBIDDEN_URL_TOKEN_PARAM_NAMES.map((name) => String(name).toLowerCase()),
);
const MAX_DEPTH = 8;

export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERN.test(key);
}

export function redactSensitiveString(value: string): string {
  let text = String(value);
  for (const pattern of TOKEN_VALUE_PATTERNS) {
    text = text.replace(pattern, '$1[REDACTED]');
  }
  return text;
}

export function sanitizeUrlForPublicSinks(value: string): string {
  const raw = String(value || '');
  if (!raw) return raw;
  const trimmed = raw.trim();
  if (!/^(https?:\/\/|\/|\?|#)/i.test(trimmed)) {
    return redactSensitiveString(raw);
  }
  try {
    const url = new URL(raw, 'https://quaom.invalid');
    url.hash = '';
    for (const name of Array.from(url.searchParams.keys())) {
      if (FORBIDDEN_URL_PARAM_NAMES.has(name.toLowerCase())) {
        url.searchParams.set(name, '[REDACTED]');
      }
    }
    const serialized =
      url.origin === 'https://quaom.invalid'
        ? `${url.pathname}${url.search}${url.hash}`
        : url.toString();
    return redactSensitiveString(serialized);
  } catch {
    const withoutHash = raw.split('#')[0] || '';
    return redactSensitiveString(withoutHash);
  }
}

export function containsForbiddenUrlToken(value: string): boolean {
  const raw = String(value || '');
  if (!raw) return false;
  if (
    /(?:^|[?#&])(manage|token|manageToken|deleteToken|claimToken|supportProof|authorization|session)=/i.test(
      raw,
    )
  ) {
    return true;
  }
  try {
    const url = new URL(raw, 'https://quaom.invalid');
    for (const name of Array.from(url.searchParams.keys())) {
      if (FORBIDDEN_URL_PARAM_NAMES.has(name.toLowerCase())) return true;
    }
    return hasSensitiveHash(url.hash);
  } catch {
    return false;
  }
}

export function assertNoTokenInUrl(value: string, sink: string): void {
  if (containsForbiddenUrlToken(value)) {
    throw new Error(`Forbidden private token material in ${sink}.`);
  }
}

export function hasSensitiveHash(hash: string): boolean {
  const raw = String(hash || '').replace(/^#/, '');
  if (!raw) return false;
  const params = new URLSearchParams(raw);
  for (const name of FORBIDDEN_URL_PARAM_NAMES) {
    if (params.has(name)) return true;
  }
  return /(?:^|[&#])(manage|token|manageToken|deleteToken|claimToken|supportProof)=/i.test(raw);
}

export function deepRedactSensitiveValues<T>(value: T, depth = 0, seen = new WeakSet<object>()): T {
  if (depth > MAX_DEPTH) return '[REDACTED_DEPTH]' as T;
  if (typeof value === 'string')
    return redactSensitiveString(sanitizeUrlForPublicSinks(value)) as T;
  if (typeof value !== 'object' || value === null) return value;
  if (seen.has(value)) return '[REDACTED_CIRCULAR]' as T;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => deepRedactSensitiveValues(item, depth + 1, seen)) as T;
  }

  const source = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(source)) {
    if (isSensitiveKey(key)) {
      result[key] = '[REDACTED]';
    } else if (/href|url|uri|route|path|location/i.test(key) && typeof item === 'string') {
      result[key] = sanitizeUrlForPublicSinks(item);
    } else {
      result[key] = deepRedactSensitiveValues(item, depth + 1, seen);
    }
  }
  return result as T;
}

export function safeStringifyRedacted(value: unknown): string {
  try {
    return JSON.stringify(deepRedactSensitiveValues(value));
  } catch {
    return redactSensitiveString(String(value));
  }
}
